import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Resolve } from '@angular/router';
import { BuyerAuthService } from '../buyer/buyer-core/buyer-authentication/services/buyer-auth/buyer-auth.service';
import { PusherService } from 'src/app/Shared/services/pusher/pusher.service';
import { isPlatformBrowser } from '@angular/common';
import { NotificationService } from '../buyer/buyer-modules/account/notification/services/notification.service';
import { NotificationWindowService } from 'src/app/Shared/_services/notification/notification.service';
import { NotificationActionType } from 'src/app/Shared/enum/notifications-actions.enum';
import { NotificationsActionsService } from '../buyer/buyer-modules/account/notification/services/notifications-actions.service';
import {
  INotification,
  NotificationWithActions,
  PusherNotificationPayload,
} from 'src/app/Shared/_models/notification.interace';
import { ToastService } from 'src/app/Shared/services/toast/toast.service';
import { AnalyticsService } from 'src/app/Shared/_services/analytics/analytics.service';

@Injectable({
  providedIn: 'root',
})
export class PublicLayoutResolver implements Resolve<void> {
  constructor(
    private auth: BuyerAuthService,
    private pusherService: PusherService,
    private notificationService: NotificationService,
    private notification: NotificationWindowService,
    private notificationsActionsService: NotificationsActionsService,
    private toastService: ToastService,
    @Inject(PLATFORM_ID) private platformId: any,
    private analyticsService: AnalyticsService
  ) {}

  private hasNotificationActions(notification: INotification): boolean {
    return !!(notification as NotificationWithActions)?.actions?.length;
  }

  private shouldAutoTriggerAction(actionType: string): boolean {
    const autoTriggerActions = [
      NotificationActionType.SHOW_CLAIM_PAYMENT_POPUP,
    ];

    return autoTriggerActions.includes(actionType as NotificationActionType);
  }

  private async handleNotificationWithActions(
    notification: NotificationWithActions
  ): Promise<void> {
    try {
      // Check if notification has auto-trigger actions
      const autoTriggerActions = notification.actions?.filter((action) =>
        this.shouldAutoTriggerAction(action.action)
      );

      if (autoTriggerActions?.length) {
        // Auto-trigger the first applicable action
        const actionToTrigger = autoTriggerActions[0];
        await this.notificationsActionsService.executeAction(
          notification,
          actionToTrigger.action,
          { translations: {}, dir: 'ltr' } // Default translations for auto-actions
        );
      }
    } catch (error) {
      console.error('Error handling notification with actions:', error);
    }
  }

  resolve(): void {
    if (
      this.auth.isLoggedInAndAuthorized() &&
      isPlatformBrowser(this.platformId)
    ) {
      // Resubscribe to notification channel when entering public layout
      this.pusherService.subscribeToPrivateChannel(
        'private-user.{user_id}',
        'notification.created',
        true,
        'Notification',
        (data: PusherNotificationPayload | INotification) => {
          console.log('Notification received in public layout:', data);

          // Track push notification received
          const notificationType = 'notification' in data && 'notification_type' in data
            ? (data as PusherNotificationPayload).notification_type?.name || 'unknown'
            : 'unknown';
          // this.analyticsService.trackPushNotificationReceived(notificationType);

          let processedNotification: INotification;

          // Check if this is the new Pusher format with notification_type
          if ('notification' in data && 'notification_type' in data) {
            // New format: process notification with notification_type
            const pusherPayload = data as PusherNotificationPayload;
            processedNotification =
              this.notificationService.processPusherNotification(pusherPayload);
          } else {
            // Legacy format: use notification as-is (backwards compatibility)
            processedNotification = data as INotification;
          }

          this.notificationService.setRecievedNotification(
            processedNotification
          );

          if (this.hasNotificationActions(processedNotification)) {
            this.handleNotificationWithActions(
              processedNotification as NotificationWithActions
            );
          }

          // Show notification alert with rendered content
          this.toastService.showNotificationAlert({
            image:
              processedNotification?.image ||
              processedNotification?.extra_data?.['image'],
            body: processedNotification?.body || '',
          });

          // Show browser notification with rendered content
          this.notification.showNotification(
            processedNotification?.title || 'Notification Update',
            {
              body: processedNotification?.body || '',
              icon: 'assets/images/x-logo.png',
            }
          );
        }
      );
    } else {
      // Ensure we unsubscribe if not logged in
      this.pusherService.unsubscribeFromPrivateChannel();
    }
  }
}
